"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.deserialize = exports.RefreshInvalidError = exports.RefreshMissingError = void 0;
const axios_1 = __importDefault(require("axios"));
const deserialize_json_api_1 = require("deserialize-json-api");
const async_await_mutex_lock_1 = require("async-await-mutex-lock");
const DOMStorage_1 = require("../Utilities/DOMStorage");
const PayloadEncryption_1 = require("../PayloadEncryption");
const defaultMimeType = "application/vnd.api+json";
const expiryThreshold = -5000;
const refreshMinInterval = 20000;
class RefreshMissingError extends Error {
    constructor() {
        super("missing tokenset");
        this.name = "RefreshMissingError";
    }
}
exports.RefreshMissingError = RefreshMissingError;
class RefreshInvalidError extends Error {
    constructor() {
        super("invalid refreshToken");
        this.name = "RefreshInvalidError";
    }
}
exports.RefreshInvalidError = RefreshInvalidError;
class JsonApiClient {
    constructor(appUrl, opts) {
        this._lastRefresh = 0;
        const keyPrefix = opts.tokenStorageKeyPrefix || "MBaaS.auth.";
        this._appUrl = appUrl;
        this._opts = opts;
        this._refreshTokenKey = keyPrefix + "refreshToken";
        this._accessTokenKey = keyPrefix + "accessToken";
        this._expiryKey = keyPrefix + "expiry";
        this._lock = new async_await_mutex_lock_1.Lock();
    }
    request(method, url, payload) {
        return __awaiter(this, void 0, void 0, function* () {
            payload = Object.assign({}, payload);
            let { params, data, baseHeaders, upload, progressCallback, auth, encryption, customMimeType, } = payload;
            if (baseHeaders === undefined)
                baseHeaders = {};
            if (upload) {
                const headers = Object.assign({}, baseHeaders);
                if (data === null || data === void 0 ? void 0 : data.type)
                    headers["Content-Type"] = data.type;
                return axios_1.default.request({
                    method,
                    url,
                    headers,
                    params,
                    data,
                    onUploadProgress: (evt) => {
                        if (progressCallback) {
                            const percentage = Math.ceil(100 * (evt.loaded / evt.total));
                            progressCallback(percentage);
                        }
                    },
                });
            }
            else {
                const headers = Object.assign(Object.assign({}, baseHeaders), { "Content-Type": customMimeType || defaultMimeType, Accept: customMimeType || defaultMimeType });
                if (auth === undefined)
                    auth = true;
                let keys;
                if (encryption)
                    keys = yield (0, PayloadEncryption_1.generateClientKey)(this._appUrl);
                const ax = axios_1.default.create();
                ax.interceptors.request.use((req) => __awaiter(this, void 0, void 0, function* () {
                    let finalReq = yield this._handleExpiry(req, auth);
                    if (encryption) {
                        if (data)
                            finalReq = yield (0, PayloadEncryption_1.encryptReq)(finalReq, keys);
                        else
                            finalReq = (0, PayloadEncryption_1.attachEphPubKey)(finalReq, keys);
                    }
                    return finalReq;
                }));
                ax.interceptors.response.use((res) => __awaiter(this, void 0, void 0, function* () {
                    let finalRes = res;
                    if (encryption)
                        finalRes = yield (0, PayloadEncryption_1.decryptRes)(finalRes, keys, this._appUrl);
                    return finalRes;
                }), (err) => __awaiter(this, void 0, void 0, function* () {
                    let finalRes = yield this._handleErrors(err, encryption, keys, data);
                    if (finalRes && encryption)
                        finalRes = yield (0, PayloadEncryption_1.decryptRes)(finalRes, keys, this._appUrl);
                    return finalRes;
                }));
                return ax.request({ method, url, headers, params, data });
            }
        });
    }
    refresh() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const encryption = (_a = this._opts.payloadEncryption) === null || _a === void 0 ? void 0 : _a.enforcement.includes("COMMON");
            let plainBody;
            let keys;
            if (encryption)
                keys = yield (0, PayloadEncryption_1.generateClientKey)(this._appUrl);
            const ax = axios_1.default.create();
            ax.interceptors.request.use((req) => __awaiter(this, void 0, void 0, function* () {
                plainBody = req.data;
                let finalReq = req;
                if (encryption)
                    finalReq = yield (0, PayloadEncryption_1.encryptReq)(finalReq, keys);
                return finalReq;
            }));
            ax.interceptors.response.use((res) => __awaiter(this, void 0, void 0, function* () {
                let finalRes = res;
                if (encryption)
                    finalRes = yield (0, PayloadEncryption_1.decryptRes)(finalRes, keys, this._appUrl);
                return finalRes;
            }), (err) => __awaiter(this, void 0, void 0, function* () {
                let finalRes = yield this._handleErrors(err, encryption, keys, plainBody);
                if (finalRes && encryption)
                    finalRes = yield (0, PayloadEncryption_1.decryptRes)(finalRes, keys, this._appUrl);
                return finalRes;
            }));
            const defaultRefresh = (refreshToken) => __awaiter(this, void 0, void 0, function* () {
                const res = yield ax.patch(this._appUrl + "/services/auth", { refreshToken }, {
                    headers: {
                        "Content-Type": defaultMimeType,
                        Accept: defaultMimeType,
                    },
                });
                const resData = res.data.data;
                this.saveTokenset({
                    rt: resData.attributes.refreshToken,
                    at: resData.attributes.accessToken,
                    exp: resData.attributes.expiry,
                });
                return resData.attributes.accessToken;
            });
            const idpRefresh = (refresh_token) => __awaiter(this, void 0, void 0, function* () {
                const res = yield ax.patch(this._appUrl + "/services/idp/auth", { refresh_token }, {
                    headers: {
                        "Content-Type": defaultMimeType,
                        Accept: defaultMimeType,
                    },
                });
                const resData = res.data.data;
                this.saveTokenset({
                    rt: resData.attributes.tokenset.refresh_token,
                    at: resData.attributes.tokenset.access_token,
                    exp: resData.attributes.tokenset.expiry,
                });
                return resData.attributes.tokenset.access_token;
            });
            yield this._lock.acquire(2);
            const interval = Date.now() - this._lastRefresh;
            if (interval < refreshMinInterval) {
                const at = this.getAccessToken();
                if (!at) {
                    this._lock.release(2);
                    throw new RefreshMissingError();
                }
                this._lock.release(2);
                return at;
            }
            const rt = this.getRefreshToken();
            if (!rt) {
                this._lock.release(2);
                throw new RefreshMissingError();
            }
            try {
                let at;
                if (this._opts.useIdentityProvider === true) {
                    at = yield idpRefresh(rt);
                }
                else {
                    at = yield defaultRefresh(rt);
                }
                this._lastRefresh = Date.now();
                this._lock.release(2);
                return at;
            }
            catch (err) {
                if (err.response.data.errors) {
                    const errCode = err.response.data.errors[0].code;
                    if (errCode === "IVREF") {
                        this.removeTokenset();
                        this._lock.release(2);
                        throw new RefreshInvalidError();
                    }
                }
                this._lock.release(2);
                throw err;
            }
        });
    }
    _handleExpiry(req, auth) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!auth)
                return req;
            yield this._lock.acquire(1);
            const expiry = this.getExpiry();
            if (expiry) {
                const unixExpiry = Date.parse(expiry);
                const diff = Date.now() - unixExpiry;
                if (diff > expiryThreshold) {
                    try {
                        yield this.refresh();
                    }
                    catch (err) {
                        this._lock.release(1);
                        const evtCb = this._opts.eventCallback;
                        if (err instanceof RefreshMissingError) {
                            if (typeof evtCb === "function")
                                yield evtCb({ name: "missing_tokenset" });
                        }
                        else if (err instanceof RefreshInvalidError) {
                            if (typeof evtCb === "function")
                                yield evtCb({ name: "invalid_refresh_token" });
                        }
                        return Promise.reject("refresh error");
                    }
                }
            }
            const accessToken = this.getAccessToken();
            if (accessToken) {
                if (req.headers)
                    req.headers.Authorization = `Bearer ${accessToken}`;
            }
            this._lock.release(1);
            return req;
        });
    }
    _handleErrors(err, encryption, keys, plainBody) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const errStatus = (_a = err.response) === null || _a === void 0 ? void 0 : _a.status;
            const errData = (_b = err.response) === null || _b === void 0 ? void 0 : _b.data;
            if (errStatus === 401 && typeof errData === "object") {
                const errCode = errData.errors[0].code;
                try {
                    if (errCode === "IVACC") {
                        const accessToken = yield this.refresh();
                        if (err.config.headers)
                            err.config.headers.Authorization = `Bearer ${accessToken}`;
                        return axios_1.default.request(err.config);
                    }
                }
                catch (refErr) {
                    const evtCb = this._opts.eventCallback;
                    if (refErr instanceof RefreshMissingError) {
                        if (typeof evtCb === "function") {
                            yield evtCb({ name: "missing_tokenset" });
                            return;
                        }
                    }
                    else if (refErr instanceof RefreshInvalidError) {
                        if (typeof evtCb === "function") {
                            yield evtCb({ name: "invalid_refresh_token" });
                            return;
                        }
                    }
                    return Promise.reject("refresh error");
                }
            }
            if (encryption &&
                keys &&
                plainBody &&
                errData === "cipher: message authentication failed") {
                keys.serverKey = yield PayloadEncryption_1.ServerPubKey.read(this._appUrl, true);
                const sharedKey = yield (0, PayloadEncryption_1.calculateSharedKey)(keys);
                const cipher = yield (0, PayloadEncryption_1.encrypt)(sharedKey, plainBody);
                err.config.data = JSON.stringify({ cipherText: cipher });
                return axios_1.default.request(err.config);
            }
            return Promise.reject(err);
        });
    }
    saveTokenset(tokenset) {
        const { localStorage } = (0, DOMStorage_1.DOMStorage)();
        localStorage.setItem(this._refreshTokenKey, tokenset.rt);
        localStorage.setItem(this._accessTokenKey, tokenset.at);
        localStorage.setItem(this._expiryKey, tokenset.exp);
    }
    removeTokenset() {
        const { localStorage } = (0, DOMStorage_1.DOMStorage)();
        localStorage.removeItem(this._refreshTokenKey);
        localStorage.removeItem(this._accessTokenKey);
        localStorage.removeItem(this._expiryKey);
    }
    getRefreshToken() {
        const { localStorage } = (0, DOMStorage_1.DOMStorage)();
        return localStorage.getItem(this._refreshTokenKey);
    }
    getAccessToken() {
        const { localStorage } = (0, DOMStorage_1.DOMStorage)();
        return localStorage.getItem(this._accessTokenKey);
    }
    getExpiry() {
        const { localStorage } = (0, DOMStorage_1.DOMStorage)();
        return localStorage.getItem(this._expiryKey);
    }
}
exports.default = JsonApiClient;
function deserialize(data, config) {
    return __awaiter(this, void 0, void 0, function* () {
        const transformKeys = (config ? config.keyForAttribute : null) || "camelCase";
        if (config && typeof config.extras === "object") {
            delete config.extras.data;
        }
        const { data: deserialized } = (0, deserialize_json_api_1.deserialize)(data, { transformKeys });
        return Object.assign({ data: deserialized }, config === null || config === void 0 ? void 0 : config.extras);
    });
}
exports.deserialize = deserialize;
